import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  Loader2,
  MapPin,
  Package,
  Pencil,
  RefreshCw,
  Settings,
  Truck,
} from "lucide-react";
import { POInlineEditableField } from "@/components/po-inline-edit";
import { LoadingButton } from "@/components/loading-button";
import { Button } from "@/components/ui/button";
import { InlineFieldInput } from "@/components/inline-field-group";
import { cn, formatMoney } from "@/lib/utils";
import { QuoteBuilderApiError, useQuoteBuilderApi } from "../api/client";
import {
  applyQuoteChange,
  applyWorkflowChange,
  quoteQueryOptions,
  workflowQueryOptions,
} from "../api/queries";
import type { Quote } from "../api/types";
import { numberValue } from "../ui/numbers";
import { PageState, SurfaceCard } from "../ui/page";
import { QuoteLineIdentity } from "../ui/product";
import { equipmentRows, quoteLineNumbers } from "../ui/quote-lines";
import type { WorkflowTabProps } from "../workflow/types";
import {
  saveShipping,
  shippingQueryOptions,
  type DeliveryAddress,
  type DeliveryContact,
  type FreightOffer,
  type Shipment,
  type ShipmentSelections,
  type ShippingChanges,
  type ShippingMethod,
  type ShippingState,
  type ShipTo,
} from "./shipping-api";
import { DeliveryChoices } from "./delivery-choices";
import { PricingFreight, RepricingLine } from "./freight-pricing";
import { ShipToRow } from "./ship-to";
import {
  deliverySummary,
  detectedChoices,
  quoteDeliveryChoices,
} from "./delivery-rules";

const shortDate = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});
const pounds = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const tierLabels: Record<FreightOffer["tier"], string> = {
  ground: "Standard",
  liftgate: "With liftgate",
  two_day: "2-Day Air",
  next_day: "Next-Day Air",
};
const missingLabels: Record<string, string> = {
  delivery_address: "Add delivery address",
  delivery_contact: "Add delivery contact",
  equipment_shipments: "Add equipment",
  shipping_method: "Choose freight method",
  shipping_confirmation: "Confirm the new freight",
};
const addressFields = [
  ["name", "Delivery site"],
  ["line1", "Street"],
  ["line2", "Suite / unit"],
  ["city", "City"],
  ["state", "State"],
  ["postal_code", "ZIP"],
] as const;
const contactFields = [
  ["name", "Contact", "text"],
  ["email", "Email", "email"],
  ["phone", "Phone", "tel"],
] as const;

function formatDate(value: string | null | undefined) {
  return value ? shortDate.format(new Date(`${value}T00:00:00Z`)) : null;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-1 text-xs font-medium text-muted-foreground">
      {label}
      {children}
    </label>
  );
}

/** Dirty fields are patches, so refreshed server values remain visible everywhere else. */
function clearSubmittedFields<T extends object>(
  draft: Partial<T>,
  submitted: Partial<T>,
): Partial<T> {
  return Object.fromEntries(
    Object.entries(draft).filter(
      ([key, value]) => value !== submitted[key as keyof T],
    ),
  ) as Partial<T>;
}

function FreightMethodOption({
  method,
  selected,
  busy,
  pending,
  onSelect,
}: {
  method: ShippingMethod;
  selected: boolean;
  busy: boolean;
  pending: boolean;
  onSelect: () => void;
}) {
  const arrival =
    method.id === "customer_pickup" ? "Pickup ready" : "Est. delivery";
  const date = formatDate(method.estimated_delivery_date);
  return (
    <button
      aria-checked={selected}
      className={cn(
        "grid w-full grid-cols-[18px_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 px-4 py-3 text-left outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset disabled:cursor-not-allowed",
        selected ? "bg-muted" : "hover:bg-muted/50",
        !method.available && "opacity-60",
      )}
      disabled={busy || !method.available}
      onClick={onSelect}
      role="radio"
      type="button"
    >
      <span
        className={cn(
          "mt-0.5 grid size-[18px] place-items-center rounded-full border",
          selected
            ? "border-primary bg-primary text-primary-foreground"
            : "border-muted-foreground/40",
        )}
      >
        {pending ? (
          <Loader2 className="size-3 animate-spin" />
        ) : selected ? (
          <Check className="size-3" />
        ) : null}
      </span>
      <span className="text-sm font-semibold">{method.label}</span>
      <span className="text-sm font-semibold tabular-nums">
        {method.available ? formatMoney(method.amount) : null}
      </span>
      <span className="col-span-2 col-start-2 text-xs text-muted-foreground">
        {method.available
          ? [
              method.provider,
              method.transit_days.label,
              date ? `${arrival} ${date}` : null,
            ]
              .filter(Boolean)
              .join(" · ")
          : method.unavailable_reason}
      </span>
    </button>
  );
}

/** Sales's own freight sell price: typed in place, then click-to-edit once chosen. */
function ManualFreightOption({
  method,
  selected,
  busy,
  pending,
  suggested,
  source,
  onSave,
}: {
  method: ShippingMethod;
  selected: boolean;
  busy: boolean;
  pending: boolean;
  /** A price to start from: the imported file's freight. */
  suggested: number | null;
  /** Where the chosen price came from, when not typed here ("From AQProj"). */
  source: string | null;
  onSave: (amount: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const parse = (raw: string) => {
    const value = raw.trim() === "" ? NaN : Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : null;
  };
  if (selected) {
    return (
      <div
        aria-checked
        aria-label={`${method.label} ${formatMoney(method.amount)}`}
        className="grid grid-cols-[18px_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 bg-muted px-4 py-3"
        role="radio"
      >
        <span className="mt-0.5 grid size-[18px] place-items-center rounded-full border border-primary bg-primary text-primary-foreground">
          {pending ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Check className="size-3" />
          )}
        </span>
        <span className="text-sm font-semibold">Freight price</span>
        <span className="-my-1 -mr-2 text-sm font-semibold tabular-nums">
          <POInlineEditableField
            align="right"
            ariaLabel="Freight price"
            disabled={busy}
            displayValue={formatMoney(method.amount)}
            kind="money"
            onConfirm={(raw) => {
              const value = parse(raw);
              if (value != null) onSave(value);
            }}
            pending={pending}
            showEditIcon
            value={method.amount}
          />
        </span>
        <span className="col-span-2 col-start-2 text-xs text-muted-foreground">
          {source ?? method.provider}
        </span>
      </div>
    );
  }
  if (draft === null) {
    return (
      <button
        aria-checked={false}
        className="grid w-full grid-cols-[18px_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 px-4 py-3 text-left outline-none transition-colors hover:bg-muted/50 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset disabled:cursor-not-allowed"
        disabled={busy}
        onClick={() => setDraft(suggested != null ? String(suggested) : "")}
        role="radio"
        type="button"
      >
        <span className="mt-0.5 size-[18px] rounded-full border border-muted-foreground/40" />
        <span className="text-sm font-semibold">{method.label}</span>
        <span className="text-sm font-semibold tabular-nums">
          {suggested != null ? formatMoney(suggested) : null}
        </span>
        <span className="col-span-2 col-start-2 text-xs text-muted-foreground">
          {suggested != null && source ? source : method.provider}
        </span>
      </button>
    );
  }
  const amount = parse(draft);
  const save = () => {
    if (amount != null) onSave(amount);
  };
  return (
    <form
      className="grid grid-cols-[18px_minmax(0,1fr)] items-start gap-x-3 gap-y-2 px-4 py-3"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <span className="mt-0.5 size-[18px] rounded-full border border-primary" />
      <label className="text-sm font-semibold" htmlFor="manual-freight-price">
        {method.label}
      </label>
      <div className="col-start-2 flex flex-wrap items-center gap-2">
        <div className="relative w-36">
          <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-sm text-muted-foreground">
            $
          </span>
          <InlineFieldInput
            autoFocus
            className="pl-6 text-right tabular-nums"
            id="manual-freight-price"
            inputMode="decimal"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setDraft(null);
            }}
            value={draft}
          />
        </div>
        <LoadingButton
          disabled={amount == null || busy}
          loading={pending}
          size="sm"
          type="submit"
        >
          Use price
        </LoadingButton>
        <Button
          disabled={pending}
          onClick={() => setDraft(null)}
          size="sm"
          type="button"
          variant="ghost"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

function CarrierOffers({ shipment }: { shipment: Shipment }) {
  return (
    <details className="group/offers border-t">
      <summary className="flex min-h-10 cursor-pointer list-none items-center gap-2 px-4 text-xs font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
        <ChevronDown
          aria-hidden="true"
          className="size-3.5 -rotate-90 transition-transform group-open/offers:rotate-0"
        />
        Carrier offers
      </summary>
      <div className="overflow-x-auto px-4 pb-3">
        <table className="w-full min-w-[520px] text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1.5 pr-3 font-medium">Service</th>
              <th className="py-1.5 pr-3 font-medium">Carrier</th>
              <th className="py-1.5 pr-3 text-right font-medium">Net</th>
              <th className="py-1.5 pr-3 text-right font-medium">Customer</th>
              <th className="py-1.5 font-medium">Transit</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {shipment.offers.map((offer) => (
              <tr key={offer.tier}>
                <td className="py-1.5 pr-3 font-medium">
                  {tierLabels[offer.tier]}
                </td>
                <td className="py-1.5 pr-3">
                  {[offer.carrier_name, offer.service_name]
                    .filter(Boolean)
                    .join(" · ") || "Manufacturer freight"}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {formatMoney(offer.net_amount)}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {formatMoney(offer.sell_amount)}
                </td>
                <td className="py-1.5">
                  {offer.transit_days != null
                    ? `${offer.transit_days} days`
                    : "-"}
                  {offer.source === "fallback" ? (
                    <span className="ml-2 text-amber-700 dark:text-amber-400">
                      Estimate
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

type ShipmentEditor = {
  editing: string | null;
  /** The quote's resolved choices a shipment's "Quote setting" stands for. */
  quoteChoices: Partial<ShipmentSelections>;
  drafts: Record<string, Partial<ShipmentSelections>>;
  busy: boolean;
  pendingKey: string | null;
  onEdit: (key: string | null) => void;
  onChange: (key: string, changes: Partial<ShipmentSelections>) => void;
  onSave: (shipment: Shipment) => void;
  onCancel: (key: string) => void;
  editingShipTo: string | null;
  onEditShipTo: (key: string | null) => void;
  onSaveShipTo: (shipment: Shipment, shipTo: ShipTo | null) => void;
};

function ManufacturerShipments({
  shipping,
  quote,
  editor,
}: {
  shipping: ShippingState;
  quote: Quote;
  editor: ShipmentEditor;
}) {
  const rows = equipmentRows(quote.lines);
  const numbers = quoteLineNumbers(rows);
  const pickup = shipping.selected_method === "customer_pickup";
  const noFreight = shipping.selected_method === "no_freight";
  // No delivery to arrange: nothing ships on a carrier USA-RS pays for.
  const noDelivery = pickup || noFreight;
  // Shipment rates are not what the quote charges under a hand-entered price or no freight.
  const unrated = noFreight || shipping.selected_method === "manual_freight";
  if (unrated && !shipping.shipments.length) return null;
  if (!shipping.shipments.length) {
    return (
      <SurfaceCard>
        <PageState
          compact
          kind="empty"
          title={shipping.pricing_error ?? "No equipment to ship"}
        />
      </SurfaceCard>
    );
  }
  return (
    <div className="grid gap-4">
      {shipping.shipments.map((shipment) => {
        const lineIds = new Set(shipment.lines.map((line) => line.line_id));
        const displayRows = rows.filter(
          (row) =>
            lineIds.has(row.id) ||
            Boolean(
              row.configuration_group &&
              row.parent_line_id &&
              lineIds.has(row.parent_line_id),
            ),
        );
        const weights = new Map(
          shipment.lines.map((line) => [line.line_id, line]),
        );
        const editKey = shipment.key;
        const editing = editor.editing === editKey;
        const draft = editor.drafts[editKey] ?? {};
        const selection = { ...shipment.selections, ...draft };
        const effective = shipment.effective_selections;
        const freight = shipment.mode === "ltl";
        // Choices are kept per manufacturer; only the ones this shipment's mode uses count.
        const customized = Object.entries(shipment.selections).some(
          ([field, value]) =>
            value != null &&
            (freight ? field !== "parcel_service" : field === "parcel_service"),
        );
        // A shipment to its own ship-to is detected at that address.
        const detection =
          shipment.address_detection ?? shipping.address_detection;
        const detectionLabel =
          detection?.destination_type === "residential"
            ? "Residential"
            : "Commercial";
        const threshold =
          shipment.free_freight_threshold != null
            ? numberValue(shipment.free_freight_threshold)
            : null;
        const remaining =
          shipment.free_freight_remaining != null
            ? numberValue(shipment.free_freight_remaining)
            : null;
        const ready = formatDate(shipment.ship_ready_date);
        const arrival = noDelivery
          ? null
          : formatDate(shipment.estimated_delivery_date);
        return (
          <section
            aria-label={`${shipment.manufacturer} shipment`}
            className="overflow-hidden rounded-xl border bg-card"
            key={shipment.key}
          >
            <details className="group/shipment">
              <summary className="flex min-h-12 cursor-pointer list-none flex-wrap items-center justify-between gap-2 bg-muted/40 px-4 py-2.5 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset [&::-webkit-details-marker]:hidden">
                <h3 className="flex min-w-0 items-center gap-2 text-sm font-semibold">
                  <ChevronDown
                    aria-hidden="true"
                    className="size-4 shrink-0 -rotate-90 transition-transform group-open/shipment:rotate-0"
                  />
                  {shipment.manufacturer}
                  <span className="text-xs font-normal text-muted-foreground">
                    {freight ? "Freight" : "Parcel"} · {shipment.line_count}{" "}
                    {shipment.line_count === 1 ? "item" : "items"} · from{" "}
                    {shipment.origin}
                  </span>
                </h3>
                <span className="text-xs text-muted-foreground">
                  Ready {ready}
                  {arrival ? ` · Est. arrival ${arrival}` : ""}
                </span>
              </summary>
              <div className="divide-y border-t">
                {displayRows.map((row) => {
                  const line = weights.get(row.id);
                  const child = Boolean(row.parent_line_id);
                  return (
                    <div
                      className={cn(
                        "flex items-center gap-3 px-4 py-2",
                        child && "bg-muted/20 pl-8",
                      )}
                      key={row.id}
                    >
                      <span className="w-8 shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
                        {numbers.get(row.id)}
                      </span>
                      {child && !line ? (
                        <span className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                          {row.configuration_group ? (
                            <Settings className="size-4 text-muted-foreground" />
                          ) : (
                            <Package className="size-4 text-muted-foreground" />
                          )}
                          <span className="truncate">
                            {row.configuration_group
                              ? `${row.configuration_group}: ${row.description}`
                              : row.description}
                          </span>
                        </span>
                      ) : (
                        <QuoteLineIdentity
                          className="min-w-0 flex-1"
                          compact
                          line={row}
                        />
                      )}
                      <span className="shrink-0 text-right text-xs tabular-nums">
                        <span className="block font-medium">
                          Qty {numberValue(row.quantity)}
                        </span>
                        <span className="block text-muted-foreground">
                          {line?.weight_lbs != null
                            ? `${pounds.format(numberValue(line.weight_lbs))} lb each${line.weight_estimated ? " (est.)" : ""}`
                            : "Ships with equipment"}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </details>

            <div className="grid gap-3 border-t px-4 py-3">
              {shipment.manufacturer_id != null ? (
                <ShipToRow
                  busy={editor.busy}
                  editing={editor.editingShipTo === editKey}
                  // A fresh form each time it opens: it starts from what is saved.
                  key={`${editKey}:${editor.editingShipTo === editKey}`}
                  onEdit={(open) => editor.onEditShipTo(open ? editKey : null)}
                  onSave={(shipTo) => editor.onSaveShipTo(shipment, shipTo)}
                  pending={editor.pendingKey === `ship-to:${editKey}`}
                  shipment={shipment}
                />
              ) : null}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <button
                  aria-expanded={editing}
                  aria-label={`Edit ${shipment.manufacturer} delivery`}
                  className="flex min-h-9 items-center gap-2 rounded-md text-left text-sm font-medium outline-none hover:text-primary focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
                  disabled={editor.busy || noDelivery}
                  onClick={() => editor.onEdit(editing ? null : editKey)}
                  type="button"
                >
                  {noDelivery ? (
                    pickup ? (
                      "Customer pickup"
                    ) : (
                      "No freight"
                    )
                  ) : (
                    <>
                      <span className="text-muted-foreground">
                        {deliverySummary(effective, freight)}
                      </span>
                      {customized ? (
                        <span className="rounded-full border px-1.5 py-px text-[11px] font-medium">
                          Shipment setting
                        </span>
                      ) : null}
                      <Pencil className="size-3.5 text-muted-foreground" />
                    </>
                  )}
                </button>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {pounds.format(numberValue(shipment.total_weight_lbs))} lb
                  {freight && shipment.pallet_count
                    ? ` · ${shipment.pallet_count} pallet${shipment.pallet_count === 1 ? "" : "s"}`
                    : ""}
                  {!freight
                    ? ` · ${shipment.handling_unit_count} box${shipment.handling_unit_count === 1 ? "" : "es"}`
                    : ""}
                </span>
              </div>

              {editing ? (
                <div className="grid gap-3 rounded-lg border bg-muted/20 p-3">
                  <DeliveryChoices
                    detectionLabel={detectionLabel}
                    disabled={editor.busy}
                    freight={freight}
                    inherited={
                      shipment.address_detection
                        ? quoteDeliveryChoices({
                            ...shipping,
                            address_detection: shipment.address_detection,
                          })
                        : editor.quoteChoices
                    }
                    onChange={(changes) => editor.onChange(editKey, changes)}
                    parcel={!freight}
                    scope="shipment"
                    value={selection}
                  />
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      disabled={editor.busy}
                      onClick={() => editor.onCancel(editKey)}
                      size="sm"
                      variant="ghost"
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      disabled={!Object.keys(draft).length}
                      loading={editor.pendingKey === editKey}
                      loadingText="Pricing..."
                      onClick={() => editor.onSave(shipment)}
                      size="sm"
                    >
                      Save delivery
                    </LoadingButton>
                  </div>
                </div>
              ) : null}

              {!noDelivery ? (
                <ul className="grid gap-1 text-xs text-muted-foreground">
                  {shipment.suggestions
                    .filter(
                      (suggestion) =>
                        suggestion.code === "liftgate" ||
                        suggestion.source === "staff",
                    )
                    .map((suggestion) => (
                      <li
                        className="flex items-start gap-2"
                        key={`${suggestion.code}-${suggestion.reason}`}
                      >
                        {suggestion.code === "liftgate" ? (
                          <Truck
                            aria-hidden="true"
                            className="mt-px size-3.5 shrink-0"
                          />
                        ) : (
                          <MapPin
                            aria-hidden="true"
                            className="mt-px size-3.5 shrink-0"
                          />
                        )}
                        {suggestion.reason}
                      </li>
                    ))}
                </ul>
              ) : null}

              {!noDelivery &&
              (shipment.free_freight_eligible ||
                threshold != null ||
                shipment.freight_policy) ? (
                <div className="grid gap-1 text-xs">
                  {shipment.free_freight_eligible ? (
                    <strong className="font-semibold text-emerald-700 dark:text-emerald-400">
                      Free freight
                    </strong>
                  ) : threshold != null ? (
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                      <strong className="font-semibold">
                        {remaining != null && remaining > 0
                          ? `${formatMoney(remaining)} Net from free freight`
                          : "Free freight threshold met"}
                      </strong>
                      <span className="tabular-nums text-muted-foreground">
                        {formatMoney(threshold - (remaining ?? 0))} /{" "}
                        {formatMoney(threshold)} Net
                      </span>
                    </div>
                  ) : null}
                  {threshold != null && !shipment.free_freight_eligible ? (
                    <progress
                      aria-label={`${shipment.manufacturer} free freight progress`}
                      className="h-1 w-full overflow-hidden rounded [&::-moz-progress-bar]:bg-emerald-600 [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-emerald-600"
                      max={threshold}
                      value={Math.max(0, threshold - (remaining ?? 0))}
                    />
                  ) : null}
                  {shipment.freight_policy ? (
                    <p className="text-muted-foreground">
                      {shipment.freight_policy}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {!unrated ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-2 text-xs tabular-nums">
                  <span className="text-muted-foreground">
                    {pickup ? "Pickup" : "Freight"}{" "}
                    <strong className="ml-1 font-semibold text-foreground">
                      {formatMoney(shipment.base_freight_amount)}
                    </strong>
                  </span>
                  {shipment.charges.map((charge) => (
                    <span className="text-muted-foreground" key={charge.code}>
                      {charge.label}{" "}
                      <strong className="ml-1 font-semibold text-foreground">
                        {charge.amount != null
                          ? `+${formatMoney(charge.amount)}`
                          : "in rate"}
                      </strong>
                    </span>
                  ))}
                  {!pickup ? (
                    <span className="text-muted-foreground">
                      Net{" "}
                      <strong className="ml-1 font-semibold text-foreground">
                        {formatMoney(shipment.net_amount)}
                      </strong>
                    </span>
                  ) : null}
                  <span className="ml-auto text-sm font-semibold">
                    {formatMoney(shipment.applied_freight_amount)}
                  </span>
                </div>
              ) : null}
            </div>
            {!noDelivery ? <CarrierOffers shipment={shipment} /> : null}
          </section>
        );
      })}
    </div>
  );
}

export function ShippingTab(props: WorkflowTabProps) {
  return <ShippingEditor key={props.quoteId} {...props} />;
}

type Save = {
  action: string;
  changes: ShippingChanges;
  address?: Partial<DeliveryAddress>;
  contact?: Partial<DeliveryContact>;
  selection?: { key: string; fields: Partial<ShipmentSelections> };
  shipTo?: string;
  notes?: string;
};

function ShippingEditor({
  active = true,
  quoteId,
  quote,
  onQuoteChange,
  onWorkflowChange,
}: WorkflowTabProps) {
  const api = useQuoteBuilderApi();
  const queryClient = useQueryClient();
  // The newest revision this tab knows of: the shell's prop or a fresher cached read.
  const currentQuote = () => {
    const cached = queryClient.getQueryData<Quote>(["quote", quoteId]);
    return cached && cached.id === quote.id && cached.revision > quote.revision
      ? cached
      : quote;
  };
  const shippingQuery = useQuery({
    ...shippingQueryOptions(api, quoteId),
    enabled: active,
  });
  const shipping = shippingQuery.data;
  const [addressDraft, setAddressDraft] = useState<Partial<DeliveryAddress>>(
    {},
  );
  const [contactDraft, setContactDraft] = useState<Partial<DeliveryContact>>(
    {},
  );
  const [selectionDrafts, setSelectionDrafts] = useState<
    Record<string, Partial<ShipmentSelections>>
  >({});
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [editingDelivery, setEditingDelivery] = useState(false);
  const [editingShipment, setEditingShipment] = useState<string | null>(null);
  const [editingShipTo, setEditingShipTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (active && shipping && quote.revision > shipping.revision) {
      void queryClient.invalidateQueries(
        { queryKey: ["quote-shipping", quoteId] },
        { cancelRefetch: false },
      );
    }
  }, [active, quote.revision, quoteId, queryClient, shipping]);

  async function reconcile() {
    const [nextQuote, nextShipping, nextWorkflow] = await Promise.all([
      queryClient.fetchQuery({
        ...quoteQueryOptions(api, quoteId),
        staleTime: 0,
      }),
      queryClient.fetchQuery({
        ...shippingQueryOptions(api, quoteId),
        staleTime: 0,
      }),
      queryClient.fetchQuery({
        ...workflowQueryOptions(api, quoteId),
        staleTime: 0,
      }),
    ]);
    queryClient.setQueryData<ShippingState>(
      ["quote-shipping", quoteId],
      (old) =>
        old && old.revision > nextShipping.revision ? old : nextShipping,
    );
    const known = currentQuote().revision;
    if (nextQuote.revision >= known) {
      applyQuoteChange(queryClient, quoteId, nextQuote);
      onQuoteChange(nextQuote);
    }
    if (nextWorkflow.revision >= Math.max(known, nextQuote.revision)) {
      applyWorkflowChange(queryClient, quoteId, nextWorkflow);
      onWorkflowChange(nextWorkflow);
    }
  }

  const mutation = useMutation({
    mutationFn: ({ changes }: Save) =>
      saveShipping(api, currentQuote(), changes),
    onMutate: () => setError(null),
    onSuccess: async (workflow, submitted) => {
      if (workflow.revision >= currentQuote().revision)
        onWorkflowChange(workflow);
      if (submitted.address)
        setAddressDraft((draft) =>
          clearSubmittedFields(draft, submitted.address!),
        );
      if (submitted.contact)
        setContactDraft((draft) =>
          clearSubmittedFields(draft, submitted.contact!),
        );
      if (submitted.selection) {
        const { key, fields } = submitted.selection;
        setSelectionDrafts((drafts) => ({
          ...drafts,
          [key]: clearSubmittedFields(drafts[key] ?? {}, fields),
        }));
        setEditingShipment((current) => (current === key ? null : current));
      }
      if (submitted.shipTo)
        setEditingShipTo((current) =>
          current === submitted.shipTo ? null : current,
        );
      if (submitted.notes !== undefined)
        setNotesDraft((draft) => (draft === submitted.notes ? null : draft));
      if (
        submitted.address ||
        submitted.contact ||
        submitted.action === "refresh-delivery"
      )
        setEditingDelivery(false);
      try {
        await reconcile();
      } catch {
        setError(
          "Shipping saved. Reload the latest totals before making another change.",
        );
      }
    },
    onError: async (caught) => {
      const conflict =
        caught instanceof QuoteBuilderApiError && caught.status === 412;
      const message =
        caught instanceof QuoteBuilderApiError &&
        caught.code === "freight_unavailable"
          ? "Freight could not be priced for this address. Check the address and try again."
          : conflict
            ? "The quote changed. Your unsaved edits are still here; review them and save again."
            : caught instanceof QuoteBuilderApiError && caught.status === 422
              ? "Review the delivery address and shipment choices, then save again."
              : "Shipping could not be saved. Your edits are still here; try again.";
      setError(message);
      if (conflict) {
        try {
          await reconcile();
        } catch {
          setError(
            "The latest shipping could not be loaded. Your edits are still here; reload and try again.",
          );
        }
      }
    },
  });

  if (shippingQuery.isError && !shipping) {
    return (
      <SurfaceCard title="Shipping">
        <PageState
          action={
            <Button
              onClick={() => void shippingQuery.refetch()}
              size="sm"
              variant="outline"
            >
              Retry
            </Button>
          }
          compact
          kind="error"
          title="Shipping could not load"
        />
      </SurfaceCard>
    );
  }
  if (!shipping) return <PricingFreight quote={quote} />;

  const address = { ...shipping.delivery_address, ...addressDraft };
  const contact = { ...shipping.delivery_contact, ...contactDraft };
  const deliveryDirty =
    Object.keys(addressDraft).length > 0 ||
    Object.keys(contactDraft).length > 0;
  const busy = mutation.isPending;
  const activeAction = busy ? mutation.variables?.action : null;
  const save = (values: Save) => {
    if (!busy) mutation.mutate(values);
  };
  const selected = shipping.methods.find(
    (method) => method.id === shipping.selected_method,
  );
  const editor: ShipmentEditor = {
    editing: editingShipment,
    quoteChoices: quoteDeliveryChoices(shipping),
    drafts: selectionDrafts,
    busy,
    pendingKey: activeAction?.startsWith("shipment:")
      ? activeAction.slice("shipment:".length)
      : null,
    onEdit: setEditingShipment,
    onChange: (key, fields) =>
      setSelectionDrafts((drafts) => ({
        ...drafts,
        [key]: { ...drafts[key], ...fields },
      })),
    onSave: (shipment) => {
      const fields = selectionDrafts[shipment.key] ?? {};
      if (shipment.manufacturer_id == null) return;
      save({
        action: `shipment:${shipment.key}`,
        changes: {
          shipment_selections: [
            { manufacturer_id: shipment.manufacturer_id, ...fields },
          ],
        },
        selection: { key: shipment.key, fields },
      });
    },
    onCancel: (key) => {
      setSelectionDrafts((drafts) => ({ ...drafts, [key]: {} }));
      setEditingShipment(null);
    },
    editingShipTo,
    onEditShipTo: setEditingShipTo,
    onSaveShipTo: (shipment, shipTo) => {
      if (shipment.manufacturer_id == null) return;
      save({
        action: `shipment:ship-to:${shipment.key}`,
        changes: {
          shipment_selections: [
            { manufacturer_id: shipment.manufacturer_id, ship_to: shipTo },
          ],
        },
        shipTo: shipment.key,
      });
    },
  };
  const imported =
    shipping.imported_freight_amount != null
      ? numberValue(shipping.imported_freight_amount)
      : null;
  const usingImported =
    imported != null &&
    shipping.freight_override != null &&
    numberValue(shipping.freight_override) === imported;
  const importLabel =
    shipping.imported_freight_source === "aqproj" ? "AQProj" : "Import";
  const manual = shipping.selected_method === "manual_freight";
  const unrated = manual || shipping.selected_method === "no_freight";
  const standard = shipping.methods.find(
    (method) => method.id === "standard_freight",
  );
  const unsaved =
    deliveryDirty ||
    Object.values(selectionDrafts).some((draft) => Object.keys(draft).length) ||
    notesDraft !== null;

  return (
    <div className="grid min-w-0 gap-4" data-shipping-editor="mounted">
      <SurfaceCard
        actions={
          <Button
            aria-expanded={editingDelivery}
            onClick={() => setEditingDelivery(!editingDelivery)}
            size="sm"
            variant="ghost"
          >
            {deliveryDirty ? "Unsaved delivery" : "Edit delivery"}
            {editingDelivery ? <ChevronDown /> : <Pencil />}
          </Button>
        }
        title="Delivery"
      >
        {editingDelivery ? (
          <div className="grid gap-4 p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              {contactFields.map(([key, label, type]) => (
                <Field key={key} label={label}>
                  <InlineFieldInput
                    className="text-foreground"
                    onChange={(event) =>
                      setContactDraft((draft) => ({
                        ...draft,
                        [key]: event.target.value,
                      }))
                    }
                    type={type}
                    value={contact[key] ?? ""}
                  />
                </Field>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-6">
              {addressFields.map(([key, label]) => (
                <div
                  className={cn(
                    key === "name" || key === "line1"
                      ? "sm:col-span-3"
                      : key === "line2" || key === "city"
                        ? "sm:col-span-2"
                        : "sm:col-span-1",
                    key === "line2" && "sm:col-span-2",
                  )}
                  key={key}
                >
                  <Field label={label}>
                    <InlineFieldInput
                      className="text-foreground"
                      onChange={(event) =>
                        setAddressDraft((draft) => ({
                          ...draft,
                          [key]: event.target.value,
                        }))
                      }
                      value={address[key] ?? ""}
                    />
                  </Field>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <LoadingButton
                disabled={deliveryDirty}
                loading={activeAction === "refresh-delivery"}
                onClick={() =>
                  save({
                    action: "refresh-delivery",
                    changes: { refresh_delivery: true },
                  })
                }
                size="sm"
                variant="outline"
              >
                <RefreshCw /> Use customer info
              </LoadingButton>
              <div className="flex gap-2">
                <Button
                  disabled={busy}
                  onClick={() => {
                    setAddressDraft({});
                    setContactDraft({});
                    setEditingDelivery(false);
                  }}
                  size="sm"
                  variant="ghost"
                >
                  Cancel
                </Button>
                <LoadingButton
                  disabled={!deliveryDirty}
                  loading={activeAction === "delivery"}
                  loadingText="Pricing..."
                  onClick={() =>
                    save({
                      action: "delivery",
                      changes: {
                        delivery_address: address,
                        delivery_contact: contact,
                      },
                      address: addressDraft,
                      contact: contactDraft,
                    })
                  }
                  size="sm"
                >
                  Save delivery
                </LoadingButton>
              </div>
            </div>
          </div>
        ) : (
          <button
            aria-label="Edit delivery address and contact"
            className="grid w-full gap-3 px-4 py-3 text-left outline-none hover:bg-muted/40 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset sm:grid-cols-2"
            onClick={() => setEditingDelivery(true)}
            type="button"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold">
                {address.name ||
                  [address.line1, address.line2].filter(Boolean).join(", ") ||
                  "Add delivery address"}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {[
                  address.name
                    ? [address.line1, address.line2].filter(Boolean).join(", ")
                    : null,
                  [
                    [address.city, address.state].filter(Boolean).join(", "),
                    address.postal_code,
                  ]
                    .filter(Boolean)
                    .join(" "),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              {shipping.address_detection ? (
                <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin aria-hidden="true" className="size-3.5" />
                  {shipping.address_detection.summary}
                </p>
              ) : null}
            </div>
            <div className="min-w-0 sm:text-right">
              <p className="text-sm font-semibold">
                {contact.name || "Add delivery contact"}
              </p>
              <p className="mt-0.5 break-words text-xs text-muted-foreground">
                {[contact.email, contact.phone].filter(Boolean).join(" · ")}
              </p>
            </div>
          </button>
        )}
      </SurfaceCard>

      <SurfaceCard title="Freight method">
        <RepricingLine active={busy || shippingQuery.isFetching} />
        <div
          aria-label="Freight method"
          className="grid divide-y lg:grid-cols-2 lg:divide-x lg:divide-y-0"
          role="radiogroup"
        >
          {shipping.methods.map((method) =>
            method.id === "manual_freight" ? (
              <ManualFreightOption
                busy={busy}
                // A fresh option when the choice changes: no half-typed price survives it.
                key={`${method.id}:${manual}`}
                method={method}
                onSave={(amount) =>
                  save({
                    action: "manual",
                    changes: {
                      selected_method: "manual_freight",
                      freight_override: amount,
                    },
                  })
                }
                pending={activeAction === "manual"}
                selected={manual}
                source={
                  manual
                    ? usingImported
                      ? `From ${importLabel}`
                      : null
                    : imported != null
                      ? `${importLabel} freight`
                      : null
                }
                suggested={manual ? null : imported}
              />
            ) : (
              <FreightMethodOption
                busy={busy}
                key={method.id}
                method={method}
                onSelect={() =>
                  save({
                    action: `method:${method.id}`,
                    changes: { selected_method: method.id },
                  })
                }
                pending={activeAction === `method:${method.id}`}
                selected={shipping.selected_method === method.id}
              />
            ),
          )}
        </div>
      </SurfaceCard>

      {shipping.shipments.length &&
      shipping.selected_method !== "customer_pickup" &&
      shipping.selected_method !== "no_freight" ? (
        <SurfaceCard title="Delivery options">
          <div className="grid gap-3 p-4">
            <p className="text-xs text-muted-foreground">
              Every shipment uses these unless you change it on the shipment.
            </p>
            <DeliveryChoices
              detectionLabel={
                shipping.address_detection?.destination_type === "residential"
                  ? "Residential"
                  : "Commercial"
              }
              disabled={busy}
              freight={shipping.shipments.some((item) => item.mode === "ltl")}
              inherited={detectedChoices(shipping)}
              onChange={(changes) =>
                save({
                  action: "defaults",
                  changes: { delivery_defaults: changes },
                })
              }
              parcel={shipping.shipments.some((item) => item.mode === "parcel")}
              scope="quote"
              value={shipping.delivery_defaults ?? {}}
            />
            {activeAction === "defaults" ? (
              <p
                className="flex items-center gap-2 text-xs text-muted-foreground"
                role="status"
              >
                <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />{" "}
                Repricing every shipment...
              </p>
            ) : null}
          </div>
        </SurfaceCard>
      ) : null}

      {error ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
          role="alert"
        >
          <span>{error}</span>
          <Button
            disabled={busy}
            onClick={() => {
              void reconcile()
                .then(() => setError(null))
                .catch(() =>
                  setError(
                    "Could not refresh shipping. Your unsaved edits are still here.",
                  ),
                );
            }}
            size="sm"
            variant="ghost"
          >
            Reload totals
          </Button>
        </div>
      ) : null}
      {shipping.requires_shipping_save ? (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
          role="status"
        >
          <span>
            {!selected?.available ? (
              "Choose an available freight method."
            ) : manual ? (
              "The equipment or address changed after the freight price was set."
            ) : (
              <>
                Freight is now{" "}
                <strong className="tabular-nums">
                  {formatMoney(selected.amount)}
                </strong>
                ; the quote charges{" "}
                <strong className="tabular-nums">
                  {formatMoney(shipping.applied_freight_amount)}
                </strong>
                .
              </>
            )}
          </span>
          {selected?.available ? (
            <div className="flex flex-wrap gap-2">
              {manual ? (
                <LoadingButton
                  loading={activeAction === "confirm"}
                  onClick={() => save({ action: "confirm", changes: {} })}
                  size="sm"
                  variant="outline"
                >
                  Keep {formatMoney(selected.amount)}
                </LoadingButton>
              ) : null}
              {!manual || standard?.available ? (
                <LoadingButton
                  loading={activeAction === "use-new"}
                  loadingText="Pricing..."
                  onClick={() =>
                    save({
                      action: "use-new",
                      changes: manual
                        ? { selected_method: "standard_freight" }
                        : {},
                    })
                  }
                  size="sm"
                >
                  Use {formatMoney(manual ? standard!.amount : selected.amount)}
                </LoadingButton>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <ManufacturerShipments
        editor={editor}
        quote={quote}
        shipping={shipping}
      />

      <div className="grid gap-4 rounded-xl border bg-card px-4 py-3 md:grid-cols-[minmax(0,1fr)_minmax(16rem,auto)]">
        <div className="grid min-w-0 content-start gap-2">
          <div className="grid gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              Shipping notes
            </span>
            <POInlineEditableField
              allowWrap
              ariaLabel="Shipping notes"
              buttonClassName="-mx-2 w-[calc(100%+1rem)] justify-between"
              kind="textarea"
              onConfirm={(raw) => {
                const next = raw.slice(0, 5000);
                setNotesDraft(next);
                save({ action: "notes", changes: { notes: next }, notes: next });
              }}
              pending={activeAction === "notes"}
              placeholder="None"
              showEditIcon
              title="Edit shipping notes"
              value={notesDraft ?? shipping.notes ?? ""}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {notesDraft !== null ? (
              <LoadingButton
                loading={activeAction === "notes"}
                onClick={() =>
                  save({
                    action: "notes",
                    changes: { notes: notesDraft },
                    notes: notesDraft,
                  })
                }
                size="sm"
                variant="outline"
              >
                Save notes
              </LoadingButton>
            ) : null}
            <p
              className={cn(
                "text-xs",
                shipping.readiness.ready
                  ? "text-emerald-700 dark:text-emerald-400"
                  : "text-amber-800 dark:text-amber-300",
              )}
              role="status"
            >
              {busy
                ? "Saving shipping..."
                : unsaved
                  ? "Unsaved changes"
                  : shipping.readiness.ready
                    ? "Shipping saved"
                    : shipping.readiness.missing
                        .map((key) => missingLabels[key] ?? "Review shipping")
                        .join(" · ")}
            </p>
          </div>
        </div>
        <dl className="grid content-start gap-1.5 text-sm tabular-nums">
          {!unrated ? (
            <>
              <div className="flex justify-between gap-8">
                <dt className="text-muted-foreground">Freight</dt>
                <dd>{formatMoney(shipping.calculated_amount)}</dd>
              </div>
              <div className="flex justify-between gap-8">
                <dt className="text-muted-foreground">Delivery extras</dt>
                <dd>{formatMoney(shipping.accessorial_amount)}</dd>
              </div>
              <div className="flex justify-between gap-8">
                <dt className="text-muted-foreground">Carrier Net</dt>
                <dd>{formatMoney(shipping.net_amount)}</dd>
              </div>
            </>
          ) : null}
          <div className="flex justify-between gap-8 font-semibold">
            <dt>
              {shipping.requires_shipping_save
                ? "Saved freight"
                : "Freight total"}
            </dt>
            <dd>{formatMoney(shipping.applied_freight_amount)}</dd>
          </div>
          <div className="mt-1 flex justify-between gap-8 border-t pt-2 font-semibold">
            <dt>Quote total</dt>
            <dd>{formatMoney(quote.totals.grand_total)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
